import {fromJS, Map} from 'immutable';
import {AnyAction, Reducer} from 'redux';
import {call, put} from 'redux-saga/effects';
import {createSelector} from 'reselect';
import {ApplicationState} from 'store/rootReducer';
import {
  AccessRequest,
  accessService,
} from 'store/services/authenticationService';
import {action} from 'typesafe-actions';

// Actions types
export enum AuthenticationTypes {
  ACCESS_REQUEST = '@authentication/ACCESS_REQUEST',
  ACCESS_SUCCESS = '@authentication/ACCESS_SUCCESS',
  ACCESS_FAILURE = '@authentication/ACCESS_FAILURE',
}

// State type
export interface AuthenticationState extends Map<any, any> {
  readonly isLoadingUserAccess: boolean;
  readonly userAccessError: boolean;
}

export const resetStore = () => action('RESET_STORE');

// Access Actions
export const accessUserRequest = (data: AccessRequest) =>
  action(AuthenticationTypes.ACCESS_REQUEST, data);

export const accessSuccess = (data) =>
  action(AuthenticationTypes.ACCESS_SUCCESS, {data});

export const accessFailure = () => action(AuthenticationTypes.ACCESS_FAILURE);

export function* accessUser(action: AnyAction) {
  try {
    const response = yield call(accessService, action.payload);
    yield put(accessSuccess(response.data));
  } catch (error) {
    yield put(accessFailure());
  }
}

// Initial state
export const INITIAL_STATE: AuthenticationState = fromJS({
  isLoadingUserAccess: false,
  userAccessError: false,
});

// Selectors
const authenticationSelector = (state: ApplicationState) =>
  state.get('authentication');

export const isLoadingUserAccess = createSelector(
  authenticationSelector,
  (auth: AuthenticationState) => auth.get('isLoadingUserAccess'),
);

export const getUserAccessError = createSelector(
  authenticationSelector,
  (auth: AuthenticationState) => auth.get('userAccessError'),
);

// Reducer
const reducer: Reducer<AuthenticationState> = (
  state = INITIAL_STATE,
  action,
) => {
  switch (action.type) {
    case AuthenticationTypes.ACCESS_REQUEST:
      return state.withMutations((prevState) =>
        prevState
          .set('isLoadingUserAccess', true)
          .set('userAccessError', false),
      );

    case AuthenticationTypes.ACCESS_SUCCESS:
      return state.withMutations((prevState) => {
        return prevState
          .set('isLoadingUserAccess', false)
          .set('userAccessError', false);
      });

    case AuthenticationTypes.ACCESS_FAILURE:
      return state.withMutations((prevState) =>
        prevState
          .set('isLoadingUserAccess', false)
          .set('userAccessError', true),
      );

    default:
      return state;
  }
};

export default reducer;
